// Challenge links: a shared result carries what the friend needs to fight the
// same opponents and beat the score: the mode, the seed (a daily exam's date
// implies its seed and rule) and the score, in one short token,
//   daily   d<v>.<day>.<rule code>.<score>.<check>
//   exam    e<v>.<exam>.<seed>.<score>.<check>
//   kumite  k<v>.<seed>.<score>.<check>
// in base 36, <v> the SIM_VERSION it was played on. The check catches typos
// and casual edits, not forgery: there's nothing to protect, scores are only
// ever compared on the friend's own screen. Pure. public/og/lib.php reads the
// same grammar for link previews: change both together (test/og.test.ts).

import { dateSeed } from '@/core/rng';
import { addDays, dayNumber, isoFromDay } from '@/core/dates';
import { ruleFor } from '@/content/daily';
import { EXAMS } from '@/content/exams';
import { SIM_VERSION } from '@/sim/sim';
import { CHANGED_IN, type Mode } from './modes';

/** The first day challenge links existed: older daily dates are no link of ours. */
const LAUNCH = '2026-10-09';
const MAX_SCORE = 9_999_999;
const MAX_SEED = 0xffffffff;

const b36 = (n: number): string => n.toString(36);

function check(body: string): string {
  return b36(dateSeed(`kiai|${body}`) % 1296).padStart(2, '0');
}

/** A whole base-36 number, or NaN. */
function num(s: string | undefined): number {
  return s !== undefined && /^[0-9a-z]{1,8}$/.test(s) ? parseInt(s, 36) : NaN;
}

/** The token for a run of `mode` played on `seed` that scored `score`. */
export function formatChallenge(mode: Mode, seed: number, score: number): string {
  const s = b36(Math.max(0, Math.min(MAX_SCORE, Math.round(score))));
  const v = b36(SIM_VERSION);
  const body =
    mode.kind === 'daily'
      ? `d${v}.${b36(dayNumber(mode.date))}.${ruleFor(mode.date).code}.${s}`
      : mode.kind === 'exam'
        ? `e${v}.${b36(mode.exam)}.${b36(seed >>> 0)}.${s}`
        : `k${v}.${b36(seed >>> 0)}.${s}`;
  return `${body}.${check(body)}`;
}

/**
 * The run a token asks for, with the friend's score to beat, or null if it
 * isn't a valid token. `stale`: made before the mode's opponents last changed
 * (CHANGED_IN), so the
 * same seed may bring other opponents.
 */
export function parseChallenge(token: string, today: string): Mode | null {
  if (!/^[a-z0-9.]{6,60}$/.test(token)) return null;
  const parts = token.split('.');
  const chk = parts.pop();
  const body = parts.join('.');
  if (chk !== check(body)) return null;
  const [head = '', ...rest] = parts;
  const version = num(head.slice(1));
  if (!Number.isFinite(version)) return null;
  const score = num(rest.at(-1));
  if (!(score >= 0 && score <= MAX_SCORE)) return null;
  const kind = head[0] === 'd' ? 'daily' : head[0] === 'e' ? 'exam' : 'kumite';
  // Stale only if this mode's opponents have changed since the link was made
  const rival = { score, ...(version < CHANGED_IN[kind] ? { stale: true } : {}) };
  switch (head[0]) {
    case 'd': {
      if (rest.length !== 3) return null;
      const day = num(rest[0]);
      if (!Number.isFinite(day)) return null;
      const date = isoFromDay(day);
      // A friend a time zone ahead may already be on tomorrow
      if (date < LAUNCH || date > addDays(today, 1)) return null;
      if (ruleFor(date).code !== rest[1]) return null;
      return { kind: 'daily', date, rival };
    }
    case 'e': {
      if (rest.length !== 3) return null;
      const exam = num(rest[0]);
      const seed = num(rest[1]);
      if (!(exam >= 0 && exam < EXAMS.length) || !(seed >= 0 && seed <= MAX_SEED)) return null;
      return { kind: 'exam', exam, seed, rival };
    }
    case 'k': {
      if (rest.length !== 2) return null;
      const seed = num(rest[0]);
      if (!(seed >= 0 && seed <= MAX_SEED)) return null;
      return { kind: 'kumite', seed, rival };
    }
    default:
      return null;
  }
}
